import { convertTimePeriodToSeconds } from "@src/cloud-connect/utils/convert-time-period-to-seconds";
import { decodeIso8601DurationToTimePeriod } from "@src/cloud-connect/utils/decode-iso-8601-duration-from-time-period";

/** One row of the event field tree. */
export interface JsonFieldItem {
  /** Identity of the node, and the string saved as the binding's `address`:
   *  a JSON Pointer (RFC 6901) relative to ONE event document, e.g.
   *  `/barrel_temperature_zones/2/actual_temperature`. */
  id: string;
  /** Default series name. */
  label: string;
  parentId: string | null;
  /** 0-based nesting depth, for indentation. */
  level: number;
  /** Folders only expand or collapse — they are never selectable. */
  isFolder: boolean;
  /** Ids of this row's children, in display order. Empty for a leaf. */
  childIds: string[];
  /** False for folders, and for a leaf whose present value the chart cannot
   *  plot (`null`). Those rows stay visible but cannot be picked. */
  isSelectable: boolean;
}

export interface JsonFieldTree {
  items: Record<string, JsonFieldItem>;
  rootIds: string[];
}

/** RFC 6901 escaping: `~` → `~0` first, then `/` → `~1`. The other order
 *  corrupts `~1` into `~01`. */
const escapePointerToken = (token: string): string =>
  token.replace(/~/g, "~0").replace(/\//g, "~1");

/** Inverse of `escapePointerToken`: `~1` → `/` first, then `~0` → `~`.
 *  Only needed to read a token back OUT of a pointer — keys taken from the
 *  document are already unescaped, and unescaping those would corrupt a key
 *  like `~1` into `/`. */
const unescapePointerToken = (token: string): string =>
  token.replace(/~1/g, "/").replace(/~0/g, "~");

/** ISO-8601 duration → seconds, reusing the existing decode + convert pair.
 *  The decoder throws on anything that is not a duration; here that just means
 *  "not a duration", so it becomes null. */
const durationToSeconds = (value: string): number | null => {
  try {
    return convertTimePeriodToSeconds(decodeIso8601DurationToTimePeriod(value));
  } catch {
    return null;
  }
};

/** A value ECharts can plot, as a number. Anything else → null.
 *  Numbers pass through, numeric strings are coerced exactly as ECharts does
 *  on a value axis, and ISO-8601 durations become seconds (ECharts would read
 *  them as NaN). */
export const parseJsonFieldValue = (value: unknown): number | null => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  // `Number("")` and `Number("  ")` are both 0 — empty is not a value.
  if (trimmed === "") {
    return null;
  }
  const seconds = durationToSeconds(trimmed);
  if (seconds !== null) {
    return seconds;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

const isContainer = (
  value: unknown,
): value is Record<string, unknown> | unknown[] =>
  typeof value === "object" && value !== null;

/** Read one value out of a document with a JSON Pointer (RFC 6901).
 *  Used by the chart: the same pointer is resolved inside every event.
 *
 *  A pointer that misses — a field the event does not carry, or an array that
 *  got shorter — yields `null` (a gap in the line), never an error: events of
 *  the same type are not obliged to carry the same keys. Tokens from an ARRAY
 *  are indices, so only there is a numeric token meaningful; object keys stay
 *  strings (JS also reorders integer-like keys, so a number would be wrong).
 *
 *  `""` is the whole document, which the picker never offers — it is a folder. */
export const resolveJsonPointer = (
  document: unknown,
  pointer: string,
): unknown => {
  if (pointer === "") {
    return document;
  }
  // A leading "/" is the separator before the first token, not a token.
  const tokens = pointer.slice(pointer.startsWith("/") ? 1 : 0).split("/");
  let current: unknown = document;
  for (const rawToken of tokens) {
    const token = unescapePointerToken(rawToken);
    if (Array.isArray(current)) {
      // RFC 6901: an array token is `0` or a digit run with no leading zero.
      // Checking the digits first matters — `Number("")` and `Number(" 1")`
      // are both numbers, so a blank token would silently read index 0.
      if (!/^(?:0|[1-9][0-9]*)$/.test(token)) {
        return null;
      }
      const index = Number(token);
      if (index >= current.length) {
        return null;
      }
      current = current[index];
      continue;
    }
    if (!isContainer(current)) {
      return null;
    }
    if (!Object.prototype.hasOwnProperty.call(current, token)) {
      return null;
    }
    current = (current as Record<string, unknown>)[token];
  }
  return current;
};

/** A series named `0` says nothing, so a numeric last token (an array index)
 *  falls back to the parent token: `/part_id/0` → `part_id`. */
const labelFromToken = (
  token: string,
  parentPointer: string,
  isArrayIndex: boolean,
): string => {
  if (!isArrayIndex) {
    // A document key is the label as-is (never unescape it: a key that is
    // literally `~1` would become `/`).
    return token;
  }
  const parentToken = parentPointer.slice(parentPointer.lastIndexOf("/") + 1);
  // The parent comes out of a pointer, so it does need unescaping.
  return parentToken ? unescapePointerToken(parentToken) : token;
};

/** Build the tree of plottable fields of one event document. Array elements
 *  are expanded by index, because a series needs exactly one number per event.
 *  Values that cannot become a series point (free text, empty or blank strings,
 *  booleans) are skipped, and so is a folder holding none of them. A `null`
 *  value is the exception: its row stays in the tree, disabled, so a field
 *  that is empty in the newest event is still discoverable.
 *  ponytail: the tree describes ONE document, the newest event. A field MISSING
 *  there is not offered even when older events carry it; a field that is NULL
 *  there is offered as a disabled row. Upgrade path: fetch a small batch and
 *  union the keys across events. */
export const buildJsonFieldTree = (document: unknown): JsonFieldTree => {
  const items: Record<string, JsonFieldItem> = {};

  const addChildren = (
    value: unknown,
    parentPointer: string,
    parentId: string | null,
    level: number,
  ): string[] => {
    const childIds: string[] = [];
    const entries: Array<[string, unknown, boolean]> = Array.isArray(value)
      ? value.map((child, index) => [String(index), child, true])
      : Object.entries(value as Record<string, unknown>).map(
          ([key, child]) => [key, child, false] as [string, unknown, boolean],
        );

    for (const [token, child, isArrayIndex] of entries) {
      const id = `${parentPointer}/${escapePointerToken(token)}`;

      if (isContainer(child)) {
        const descendantIds = addChildren(child, id, id, level + 1);
        // A folder holding nothing plottable is noise.
        if (descendantIds.length === 0) {
          continue;
        }
        items[id] = {
          id,
          label: token,
          parentId,
          level,
          isFolder: true,
          isSelectable: false,
          childIds: descendantIds,
        };
        childIds.push(id);
        continue;
      }

      const parsed = parseJsonFieldValue(child);
      // `null` is the one unplottable value that keeps its row (disabled);
      // everything else unplottable is dropped.
      if (parsed === null && child !== null) {
        continue;
      }

      items[id] = {
        id,
        label: labelFromToken(token, parentPointer, isArrayIndex),
        parentId,
        level,
        isFolder: false,
        isSelectable: parsed !== null,
        childIds: [],
      };
      childIds.push(id);
    }

    return childIds;
  };

  const rootIds = isContainer(document)
    ? addChildren(document, "", null, 0)
    : [];

  return { items, rootIds };
};
